import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useMemo } from "react";
import t from "../translations";
import RemoveIconButton from "./RemoveIconButton";
import {
  newWizardParameter,
  removeByKey,
  updateByKey,
  type ParameterKind,
  type WizardParameter,
} from "./clearanceRulesConfig";
import { duplicateParameterNames } from "./ruleDuplicateChecks";

type Props = {
  parameters: WizardParameter[];
  onChange: (parameters: WizardParameter[]) => void;
};

function ParameterGroup({
  kind,
  title,
  parameters,
  duplicates,
  onChange,
}: {
  kind: ParameterKind;
  title: string;
  parameters: WizardParameter[];
  duplicates: Set<string>;
  onChange: (parameters: WizardParameter[]) => void;
}) {
  const group = parameters.filter((param) => param.kind === kind);

  const addParameter = () => onChange([...parameters, newWizardParameter(kind)]);
  const updateParameter = (key: string, updated: WizardParameter) =>
    onChange(updateByKey(parameters, key, updated));
  const removeParameter = (key: string) => onChange(removeByKey(parameters, key));

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {group.map((param, index) => {
          const isDuplicate = param.name !== "" && duplicates.has(param.name);
          return (
            <Stack
              key={param.key}
              direction="row"
              spacing={0.5}
              sx={{
                alignItems: "flex-start",
                p: 0.5,
                borderRadius: 1,
                bgcolor: index % 2 === 1 ? "grey.100" : "background.paper",
              }}
            >
              <TextField
                size="small"
                label={t.ParameterNameLabel}
                value={param.name}
                onChange={(event) =>
                  updateParameter(param.key, {
                    ...param,
                    // A saved reference is only ever parsed back when it
                    // matches `$[A-Za-z0-9_]+` (see isParamToken) - restrict
                    // input to that alphabet so a name can't be typed that
                    // saves a reference this editor can't read back.
                    name: event.target.value.replace(/[^A-Za-z0-9_]/g, ""),
                  })
                }
                error={isDuplicate}
                helperText={isDuplicate ? t.DuplicateParameterNameError : undefined}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                type="number"
                label={t.ParameterValueLabel}
                value={param.value}
                onChange={(event) =>
                  updateParameter(param.key, { ...param, value: Number(event.target.value) })
                }
                sx={{ width: 100 }}
              />
              <RemoveIconButton
                aria-label={t.RemoveParameterButton}
                onClick={() => removeParameter(param.key)}
                sx={{ mt: 0.5 }}
              />
            </Stack>
          );
        })}
      </Box>
      <Button size="small" startIcon={<AddIcon />} onClick={addParameter}>
        {t.AddParameterButton}
      </Button>
    </Box>
  );
}

export default function ParametersSection({ parameters, onChange }: Props) {
  const duplicates = useMemo(() => duplicateParameterNames(parameters), [parameters]);

  return (
    <Box data-testid="parameters-section">
      <ParameterGroup
        kind="metric"
        title={t.MetricParametersTitle}
        parameters={parameters}
        duplicates={duplicates}
        onChange={onChange}
      />
      <ParameterGroup
        kind="non_metric"
        title={t.NonMetricParametersTitle}
        parameters={parameters}
        duplicates={duplicates}
        onChange={onChange}
      />
    </Box>
  );
}
